import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  type OnDestroy,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { PracticeSelectorComponent } from '@principle-theorem/ng-principle-shared';
import { Organisation } from '@principle-theorem/principle-core';
import {
  type IBrand,
  IOrganisation,
  type IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  filterUndefined,
  isSameRef,
  type WithRef,
} from '@principle-theorem/shared';
import { type Observable, ReplaySubject, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { TwilioPracticeNumberFormGroup } from '../phone-numbers-form';

@Component({
  selector: 'pr-practice-phone-numbers-input',
  templateUrl: './practice-phone-numbers-input.component.html',
  styleUrls: ['./practice-phone-numbers-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PracticeSelectorComponent,
    MatFormField,
    MatLabel,
    MatInput,
    MatHint,
  ],
})
export class PracticePhoneNumbersInputComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  brands$: Observable<WithRef<IBrand>[]>;
  selectedPractice$ = new ReplaySubject<WithRef<IPractice>>(1);
  form = new TwilioPracticeNumberFormGroup();
  organisation = input.required<WithRef<IOrganisation>>();

  constructor() {
    this.brands$ = toObservable(this.organisation).pipe(
      filterUndefined(),
      switchMap((organisation) => Organisation.brands$(organisation))
    );

    this.selectedPractice$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((practice) =>
        this.form.controls.practiceRef.setValue(practice.ref)
      );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  isSelectedPracticeRef(
    practiceRef: DocumentReference<IPractice>,
    selectedPracticeRef: DocumentReference<IPractice>
  ): boolean {
    return isSameRef(practiceRef, selectedPracticeRef);
  }
}
